/**
 * KNK-1.14 / FR-12 — Door seeding, and the Door map's empty state.
 *
 * The decisions and the copy, with no React, so they can be tested. The data:
 *   request    POST {VITE_PYTHON_BACKEND_URL}/api/v1/door-seeding/runs   (queued as the caller)
 *   progress   `door_seed_runs`, readable by territory.manage holders of the Account (RLS)
 *   empty map  `door_seed_coverage(south, west, north, east)`
 *   Doors      `door_map_in_bbox(...)` (KNK-1.9)
 * Migration: supabase/migrations/20261005a5_knk_1_14_door_seeding.sql.
 *
 * ⚠️ THIS IS NOT THE GATE. The database refuses a seed without territory.manage, with Knock
 * Mode off, with a malformed area, or while another run is active. What this file decides is
 * what to RENDER, and its input checks only save a round trip: they restate the database's
 * rules, they do not replace them.
 */
import type { KnockModeStatus } from './knockMode';

export type SeedKind = 'area' | 'zip' | 'city';
export type SeedStatus = 'queued' | 'running' | 'complete' | 'failed';

export interface Bounds {
  south: number;
  west: number;
  north: number;
  east: number;
}

export interface DoorSeedRun {
  id: string;
  status: SeedStatus;
  label: string;
  areaKind: SeedKind;
  sourceRelease: string | null;
  pointsFound: number;
  doorsCreated: number;
  doorsMatched: number;
  unitsCreated: number;
  unitsMatched: number;
  geocodesFilled: number;
  skipped: number;
  costUsd: number;
  costPer10kUsd: number | null;
  error: string | null;
  createdAt: string;
  finishedAt: string | null;
}

export interface DoorSeedCoverage {
  doors: number;
  seededDoors: number;
  canSeed: boolean;
  lastRun: { id: string; status: SeedStatus; label: string } | null;
}

/** One run's area is at most this many degrees on a side (the database's own limit). */
export const MAX_SEED_SPAN_DEGREES = 0.3;
/** Below this zoom the map shows counts only: one marker per Door is street-level detail. */
export const DOOR_MAP_MIN_ZOOM = 15;

export const DOOR_SEED_COPY = {
  pageTitle: 'Door map',
  pageSubtitle: 'Every Door in your account. Seed an area so reps start on a full map.',
  off: 'Knock Mode is not available for this account.',
  noToken: 'The map is not configured (no Mapbox token).',
  emptyTitle: 'No Doors here yet',
  emptyBodyManager: 'Nothing is seeded in this view. Seed it and every address in it becomes a Door.',
  emptyBodyRep: 'Nothing is seeded in this view yet. A manager can seed it from the Door map.',
  emptyBodyTooWide: 'Nothing is seeded in this view. Zoom in to seed it, or seed a ZIP or city.',
  emptyBodySeeding: (label: string) => `Seeding ${label} now. Doors appear here when it finishes.`,
  seedView: 'Seed this view',
  zoomIn: 'Zoom in to seed',
  zoomForDoors: 'Zoom in to see each Door.',
  inView: (doors: number) => `${doors.toLocaleString()} ${doors === 1 ? 'Door' : 'Doors'} in view`,
  seedTitle: 'Seed Doors',
  seedHelp:
    'Doors come from public address points (Overture, which carries the National Address Database). Apartments arrive as Units. A Door already on the map is never moved.',
  drawArea: 'Draw an area',
  drawHint: 'Click to add corners; click the first corner to finish.',
  seedDrawn: 'Seed drawn area',
  clearDrawn: 'Clear',
  zipLabel: 'ZIP code',
  seedZip: 'Seed ZIP',
  cityLabel: 'City',
  stateLabel: 'State',
  seedCity: 'Seed city',
  zipInvalid: 'A ZIP code is five digits.',
  cityMissing: 'Name the city, for example Tempe.',
  stateMissing: "Choose the city's state.",
  queued: 'Waiting to start.',
  running: 'Reading addresses and adding Doors. This takes about a minute.',
  noAddresses: 'No addresses were found in this area. Try a larger area, or one with homes in it.',
  runsTitle: 'Recent seeding',
  noRuns: 'Nothing seeded yet.',
  started: 'Seeding started',
  notStarted: 'Not seeded',
} as const;

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
const num = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? v : typeof v === 'string' && v !== '' && Number.isFinite(Number(v)) ? Number(v) : 0);
const numOrNull = (v: unknown): number | null => (v === null || v === undefined ? null : num(v));
const str = (v: unknown): string | null => (typeof v === 'string' && v.length > 0 ? v : null);
const STATUSES: readonly SeedStatus[] = ['queued', 'running', 'complete', 'failed'];
const KINDS: readonly SeedKind[] = ['area', 'zip', 'city'];

export function parseSeedRun(raw: unknown): DoorSeedRun | null {
  if (!isRecord(raw)) return null;
  const id = str(raw.id);
  const status = STATUSES.find((s) => s === raw.status);
  const areaKind = KINDS.find((k) => k === raw.area_kind);
  if (!id || !status || !areaKind) return null;
  return {
    id,
    status,
    label: str(raw.label) ?? '',
    areaKind,
    sourceRelease: str(raw.source_release),
    pointsFound: num(raw.points_found),
    doorsCreated: num(raw.doors_created),
    doorsMatched: num(raw.doors_matched),
    unitsCreated: num(raw.units_created),
    unitsMatched: num(raw.units_matched),
    geocodesFilled: num(raw.geocodes_filled),
    skipped: num(raw.skipped),
    costUsd: num(raw.cost_usd),
    costPer10kUsd: numOrNull(raw.cost_per_10k_usd),
    error: str(raw.error),
    createdAt: str(raw.created_at) ?? '',
    finishedAt: str(raw.finished_at),
  };
}

/** Fails closed: a malformed answer never turns into an offer to seed. */
export function parseCoverage(raw: unknown): DoorSeedCoverage {
  if (!isRecord(raw)) return { doors: 0, seededDoors: 0, canSeed: false, lastRun: null };
  const last = isRecord(raw.last_run) ? raw.last_run : null;
  const lastStatus = last ? STATUSES.find((s) => s === last.status) : undefined;
  return {
    doors: num(raw.doors),
    seededDoors: num(raw.seeded_doors),
    canSeed: raw.can_seed === true,
    lastRun: last && lastStatus && str(last.id) ? { id: str(last.id)!, status: lastStatus, label: str(last.label) ?? '' } : null,
  };
}

export function runIsActive(run: { status: SeedStatus | string }): boolean {
  return run.status === 'queued' || run.status === 'running';
}

const plural = (n: number, one: string, many: string) => `${n.toLocaleString()} ${n === 1 ? one : many}`;

export function runSummary(run: DoorSeedRun): string {
  if (run.status === 'failed') return run.error ?? 'Seeding failed.';
  if (run.status === 'queued') return DOOR_SEED_COPY.queued;
  if (run.status === 'running') return DOOR_SEED_COPY.running;
  const added = run.doorsCreated + run.unitsCreated;
  const already = run.doorsMatched + run.unitsMatched;
  if (run.pointsFound === 0) return DOOR_SEED_COPY.noAddresses;
  if (added === 0) {
    return `Nothing new: all ${run.pointsFound.toLocaleString()} were already on the map.`;
  }
  const parts = [`${plural(run.doorsCreated, 'Door', 'Doors')} and ${plural(run.unitsCreated, 'Unit', 'Units')} added.`];
  if (already > 0) parts.push(`${already.toLocaleString()} ${already === 1 ? 'was' : 'were'} already on the map.`);
  parts.push(`${plural(run.pointsFound, 'address', 'addresses')} read.`);
  if (run.costPer10kUsd !== null) parts.push(`$${run.costPer10kUsd.toFixed(2)} per 10,000 Doors.`);
  return parts.join(' ');
}

export function doorMapVisible(input: {
  status: KnockModeStatus;
  loading: boolean;
  canView: boolean;
  canSeed: boolean;
}): boolean {
  if (input.loading || !input.status.enabled) return false;
  return input.canView || input.canSeed;
}

export function boundsTooLargeToSeed(b: Bounds): boolean {
  return b.north - b.south > MAX_SEED_SPAN_DEGREES || b.east - b.west > MAX_SEED_SPAN_DEGREES;
}

/** The view as the GeoJSON Polygon `request_door_seed('area', ...)` takes. Left open: the
 *  database closes a ring itself. */
export function ringFromBounds(b: Bounds): { type: 'Polygon'; coordinates: number[][][] } {
  return {
    type: 'Polygon',
    coordinates: [[[b.west, b.south], [b.east, b.south], [b.east, b.north], [b.west, b.north]]],
  };
}

export type EmptyState =
  | { kind: 'doors' }
  | { kind: 'empty'; title: string; body: string; action: 'seed_view' | 'zoom_in' | null };

export function doorMapEmptyState(coverage: DoorSeedCoverage, view: Bounds, _zoom: number): EmptyState {
  if (coverage.doors > 0) return { kind: 'doors' };
  const title = DOOR_SEED_COPY.emptyTitle;
  if (!coverage.canSeed) return { kind: 'empty', title, body: DOOR_SEED_COPY.emptyBodyRep, action: null };
  if (coverage.lastRun && runIsActive(coverage.lastRun)) {
    return { kind: 'empty', title, body: DOOR_SEED_COPY.emptyBodySeeding(coverage.lastRun.label), action: null };
  }
  if (boundsTooLargeToSeed(view)) return { kind: 'empty', title, body: DOOR_SEED_COPY.emptyBodyTooWide, action: 'zoom_in' };
  return { kind: 'empty', title, body: DOOR_SEED_COPY.emptyBodyManager, action: 'seed_view' };
}

export function zipError(zip: string): string | null {
  return /^[0-9]{5}$/.test(zip.trim()) ? null : DOOR_SEED_COPY.zipInvalid;
}

export function cityError(city: string, state: string): string | null {
  if (!city.trim()) return DOOR_SEED_COPY.cityMissing;
  if (!state.trim()) return DOOR_SEED_COPY.stateMissing;
  return null;
}

export const US_STATES: readonly string[] = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'DC', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS',
  'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC',
  'ND', 'OH', 'OK', 'OR', 'PA', 'PR', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
];
